import React from "react";
import styled from "styled-components";
import moment from "moment";

import {
  Button,
  Card,
  SectionHeader,
  Skeleton,
  Tag,
  TagVariant,
} from "../../../../design-system";
import { OutreachRail } from "../../../../types/OutreachMetrics";
import { AnalysisData } from "./analysisData";
import {
  CHANNEL_COLOR,
  CHANNEL_LABEL,
  STATUS_HINT,
  STATUS_VARIANT,
} from "./RailCapacity";

// One capacity view.
//
// This used to be two: a table on the analysis tab and a stack of bars on the
// ops tab. Both iterated the same rails map out of the same snapshot document
// and printed the same allowed and delivered, so the reader had to hold two
// layouts of one fact. The bar now lives inside the delivered cell.

const Summary = styled.p`
  margin: var(--space-4) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const Big = styled.strong`
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  font-variant-numeric: tabular-nums;
`;

const Warn = styled(Summary)`
  color: var(--color-warning);
`;

const TableScroll = styled.div`
  overflow-x: auto;
  margin-top: var(--space-4);
`;

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);

  th {
    text-align: left;
    font-weight: var(--font-weight-medium);
    color: var(--color-text-tertiary);
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 0 var(--space-3) var(--space-2) 0;
    border-bottom: 1px solid var(--color-border);
    white-space: nowrap;
  }

  td {
    padding: var(--space-3) var(--space-3) var(--space-3) 0;
    border-bottom: 1px solid var(--color-border-subtle);
    color: var(--color-text-secondary);
    vertical-align: top;
  }

  td:first-child {
    color: var(--color-text);
  }
`;

const RailName = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: none;
  background: ${(p) => p.$color};
`;

const Sub = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 2px;
`;

const Numeric = styled.td`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

// The delivered cell carries its own bar, so the count and the shape of the
// count are read in one place instead of two panels.
const DeliveredCell = styled.td`
  min-width: 130px;
  font-variant-numeric: tabular-nums;
`;

const Count = styled.div`
  color: var(--color-text);
  white-space: nowrap;
`;

const NoteCell = styled.td`
  min-width: 220px;
  line-height: 1.5;
`;

const Message = styled.p`
  margin: var(--space-4) 0 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

const channelColor = (channel: string): string =>
  CHANNEL_COLOR[channel] ?? "var(--color-text-tertiary)";

const statusVariant = (status: string): TagVariant =>
  STATUS_VARIANT[status] ?? "neutral";

const Bar: React.FC<{ rail: OutreachRail }> = ({ rail }) => {
  const allowed = Math.max(rail.allowed ?? 0, 0);
  const delivered = Math.max(rail.delivered ?? 0, 0);
  // A rail that delivered more than its allowance still fills the track once.
  const width = allowed > 0 ? 100 * Math.min(delivered / allowed, 1) : 0;
  return (
    <svg
      viewBox="0 0 100 6"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ width: "100%", height: 6, display: "block", marginTop: 6 }}
    >
      <rect
        x="0"
        y="0"
        width="100"
        height="6"
        rx="2"
        fill="var(--color-surface-inset)"
      />
      {width > 0 && (
        <rect
          x="0"
          y="0"
          width={width}
          height="6"
          rx="2"
          fill={channelColor(rail.channel)}
        />
      )}
    </svg>
  );
};

export type RailTableProps = {
  analysis: AnalysisData;
  onOpenDetail: () => void;
};

const RailTable: React.FC<RailTableProps> = ({ analysis, onOpenDetail }) => {
  const { rails, railDay, railDayMatches, current, analyses } = analysis;
  // The table used to live inside the analysis panel, which skeletoned the whole
  // card while the fetch ran. As a sibling it needs its own: without this it
  // prints "no rail snapshot for this day yet" on every load, and on an account
  // switch it prints the PREVIOUS community's rails as if they were today's.
  const loading = analyses === null;

  const allowed = railDay?.totals?.allowed ?? 0;
  const delivered = railDay?.totals?.delivered ?? 0;
  const pct = allowed > 0 ? Math.round((delivered / allowed) * 100) : 0;
  const attention = rails.filter((r) =>
    ["silent", "dead", "blocked"].includes(r.status)
  ).length;

  return (
    <Card padding="lg">
      <SectionHeader
        title="Rails"
        description="What each send lane was allowed to do, what it delivered, and what its health check does not tell you."
        actions={
          <Button variant="ghost" size="sm" onClick={onOpenDetail}>
            Rail detail
          </Button>
        }
      />

      {loading ? (
        <div style={{ marginTop: "var(--space-4)" }}>
          <Skeleton width="100%" height={180} />
        </div>
      ) : rails.length === 0 ? (
        <Message>
          No rail snapshot for this day yet. The queue above still applies.
        </Message>
      ) : (
        <>
          <Summary>
            <Big>
              {delivered} of {allowed}
            </Big>{" "}
            allowed touches went out on {railDay?.date} UTC. That is {pct}%
            across {rails.length} rails
            {attention > 0 ? `, and ${attention} need a person` : ""}.
          </Summary>

          {!railDayMatches && railDay && current && (
            <Warn>
              No rail snapshot for {current.date}. These numbers are from{" "}
              {railDay.date} UTC, so the notes that analysis wrote about the
              rails are not shown beside them.
            </Warn>
          )}

          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>Rail</th>
                  <th>Status</th>
                  <th>Delivered</th>
                  <th>Queue</th>
                  <th>Last delivery</th>
                  {railDayMatches && <th>What the check hides</th>}
                </tr>
              </thead>
              <tbody>
                {rails.map((rail) => (
                  <tr key={rail.id}>
                    <td>
                      <RailName>
                        <Swatch $color={channelColor(rail.channel)} />
                        {rail.label}
                      </RailName>
                      <Sub>
                        {CHANNEL_LABEL[rail.channel] ?? rail.channel} ·{" "}
                        {rail.account} · {rail.unit}
                      </Sub>
                    </td>
                    <td>
                      <Tag variant={statusVariant(rail.status)} size="sm">
                        {rail.status}
                      </Tag>
                      <Sub>{STATUS_HINT[rail.status] ?? ""}</Sub>
                    </td>
                    <DeliveredCell>
                      <Count>
                        {rail.delivered} of {rail.allowed}
                      </Count>
                      <Bar rail={rail} />
                    </DeliveredCell>
                    <Numeric>
                      {typeof rail.queueBehind === "number"
                        ? `${rail.queueBehind} behind`
                        : "none"}
                      <Sub>
                        {typeof rail.freshCopy === "number"
                          ? `${rail.freshCopy} fresh copy`
                          : ""}
                      </Sub>
                    </Numeric>
                    <Numeric title={rail.lastDeliveredAt || undefined}>
                      {rail.lastDeliveredAt
                        ? moment(rail.lastDeliveredAt).fromNow()
                        : "never"}
                    </Numeric>
                    {railDayMatches && (
                      <NoteCell>
                        {current?.railNotes?.[rail.id] || rail.statusReason || ""}
                      </NoteCell>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </>
      )}
    </Card>
  );
};

export default RailTable;
